/**
 * El estado de un analito: alto, bajo, normal o sin clasificar.
 *
 * Los laboratorios firman «no publico rango para este parámetro» con un centinela como
 * `0 - 0`. Leerlo como un intervalo real deja TODO valor positivo fuera de rango: un
 * colesterol de 164 mg/dL marcado normal por el laboratorio se pintaba en rojo, igual que
 * los eritrocitos del sedimento urinario (2 HPF, rango `0 - 0`, flag `N`). Medido el
 * 2026-10-28 sobre los 35 080 analitos de producción: 7 663 traen el centinela y **4 246
 * salían en rojo contra el criterio del propio laboratorio**, en todas las empresas.
 *
 * El backend ya lo trataba —`_lab_reference_range` en `interpretacion/prompt_builder.py`
 * omite el centinela, y por eso el modelo nunca vio esos valores como anormales—, así que
 * esto no cambia ninguna interpretación: alinea el panel con lo que el backend ya hacía.
 *
 * Orden de decisión, y es deliberado:
 *   1. Un rango utilizable decide (es el dato más específico del propio laboratorio).
 *   2. Sin rango utilizable, manda la bandera del laboratorio.
 *   3. Sin ninguna de las dos, `unknown`: ni verde ni rojo. Afirmar «normal» sin con qué
 *      compararlo es la misma mentira que afirmar «alto».
 */

export type AnalyteStatus = 'low' | 'high' | 'normal' | 'unknown';

// Las tres grafías con que un laboratorio dice «nada que ver aquí». Misma lista que
// `_NORMAL_LAB_FLAGS` en el backend (`shared/services/lab_guide_discrepancy.py`).
const NORMAL_LAB_FLAGS = new Set(['', 'N', 'NORMAL']);

const toFiniteNumber = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) ? value : null;
};

/** El intervalo del laboratorio, o `null` cuando no sirve para clasificar. */
export const usableRange = (analyte: {
  reference_min?: unknown;
  reference_max?: unknown;
}): { min: number | null; max: number | null } | null => {
  const min = toFiniteNumber(analyte?.reference_min);
  const max = toFiniteNumber(analyte?.reference_max);
  if (min === null && max === null) return null;
  // `0 - 0` y cualquier intervalo de amplitud cero: centinela, no rango.
  if (min !== null && max !== null && min >= max) return null;
  return { min, max };
};

export const analyteStatus = (analyte: {
  value?: unknown;
  flag?: unknown;
  reference_min?: unknown;
  reference_max?: unknown;
}): AnalyteStatus => {
  const value = toFiniteNumber(analyte?.value);
  const range = usableRange(analyte);

  if (value !== null && range) {
    if (range.min !== null && value < range.min) return 'low';
    if (range.max !== null && value > range.max) return 'high';
    return 'normal';
  }

  const flag = String(analyte?.flag ?? '').trim().toUpperCase();
  if (!flag) return 'unknown';
  if (NORMAL_LAB_FLAGS.has(flag)) return 'normal';
  // `L` es la grafía de «bajo»; cualquier otra bandera es señal sin dirección conocida.
  return flag === 'L' ? 'low' : 'high';
};

/** Posición en la barra, o `null` cuando no hay un rango con el que situarla. */
export const rangePercentage = (analyte: {
  value?: unknown;
  reference_min?: unknown;
  reference_max?: unknown;
}): number | null => {
  const value = toFiniteNumber(analyte?.value);
  const range = usableRange(analyte);
  if (value === null || !range || range.min === null || range.max === null) return null;
  const position = ((value - range.min) / (range.max - range.min)) * 100;
  return Math.min(Math.max(position, 0), 100);
};
